/**
 * @file product-pickup-webcomponents.js
 * @description Product Pickup complete suite.
 * Combines PickupConfig, PickupContext, VariantChangeMediator, PickupRules,
 * PickupRuleFactory, PICKUP_REGIONS, and ProductPickup Web Components into a unified script.
 *
 * Rules applied:
 * - JSDoc documentation on all classes and methods.
 * - Explanatory comment before each line of code.
 * - Blank space after each line of code.
 */

// Universal Module Definition wrapper for browser environments
(function () {

  // Enable strict mode for secure execution
  'use strict';

  /* ==========================================================================
     SECTION 1: PICKUP CONFIGURATION & PURE HELPERS
     ========================================================================== */

  /**
   * Default time slots for Monday through Saturday.
   * @constant {string[]}
   */
  const DEFAULT_SLOTS_MON_SAT = [

    // Morning slot 08:30 AM
    '08:30 AM - 09:00 AM',

    // Morning slot 09:30 AM
    '09:30 AM - 10:00 AM',

    // Morning slot 10:30 AM
    '10:30 AM - 11:00 AM',

    // Morning slot 11:30 AM
    '11:30 AM - 12:00 PM',

    // Afternoon slot 12:30 PM
    '12:30 PM - 01:00 PM',

    // Afternoon slot 01:30 PM
    '01:30 PM - 02:00 PM',

    // Afternoon slot 02:30 PM
    '02:30 PM - 03:00 PM',

    // Afternoon slot 03:30 PM
    '03:30 PM - 04:00 PM',

    // Late afternoon slot 04:30 PM
    '04:30 PM - 05:00 PM',

    // Evening slot 05:30 PM
    '05:30 PM - 06:00 PM',

    // Evening slot 06:00 PM
    '06:00 PM - 06:30 PM'

  ];

  /**
   * Default time slots for Sunday.
   * @constant {string[]}
   */
  const DEFAULT_SLOTS_SUNDAY = [

    // Sunday morning slot 10:30 AM
    '10:30 AM - 11:00 AM',

    // Sunday morning slot 11:30 AM
    '11:30 AM - 12:00 PM',

    // Sunday afternoon slot 12:30 PM
    '12:30 PM - 01:00 PM',

    // Sunday afternoon slot 01:30 PM
    '01:30 PM - 02:00 PM',

    // Sunday afternoon slot 02:00 PM
    '02:00 PM - 02:30 PM'

  ];

  /**
   * Restricted 4:00 PM afternoon time slots for Monday through Saturday.
   * @constant {string[]}
   */
  const SLOTS_4PM_MON_SAT = [

    // Afternoon slot 04:00 PM
    '04:00 PM - 04:30 PM',

    // Afternoon slot 04:30 PM
    '04:30 PM - 05:00 PM',

    // Evening slot 05:00 PM
    '05:00 PM - 05:30 PM',

    // Evening slot 05:30 PM
    '05:30 PM - 06:00 PM',

    // Evening slot 06:00 PM
    '06:00 PM - 06:30 PM'

  ];

  /**
   * Lowercase weekday name strings indexed from Sunday (0) to Saturday (6).
   * @constant {string[]}
   */
  const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  /**
   * Normalizes variant title strings by removing quotes, unit symbols, and extra spaces.
   *
   * @param {string} [title=""] - Raw variant title string.
   * @returns {string} Normalized variant title.
   */
  function normalizeVariantTitle(title = '') {

    // Return empty string if title is falsy
    if (!title) return '';

    // Convert string to lowercase
    let t = String(title).toLowerCase();

    // Strip quotation characters
    t = t.replace(/["'”“’]/g, '');

    // Normalize inch and inches wording
    t = t.replace(/\b(inch|inches|in)\b/g, '');

    // Collapse multiple whitespace characters into a single space and trim
    t = t.replace(/\s+/g, ' ').trim();

    // Return normalized title string
    return t;

  }

  /**
   * Evaluates variant size and classifies it into a cake rule key.
   *
   * @param {string} [title=""] - Variant title string.
   * @returns {string|null} Rule identifier ("cake6_8", "cake10_quarter", "cake_half_full") or null.
   */
  function getVariantRule(title = '') {

    // Normalize incoming variant title
    const n = normalizeVariantTitle(title);

    // Check exact matches for 6 or 8
    if (n === '6' || n === '8' || /^6$/.test(n) || /^8$/.test(n)) {

      // Return 6 to 8 inch rule key
      return 'cake6_8';

    }

    // Check word boundaries for 6 excluding sheet cakes
    if (n.match(/^6\b/) && !n.includes('sheet')) return 'cake6_8';

    // Check word boundaries for 8 excluding sheet cakes
    if (n.match(/^8\b/) && !n.includes('sheet')) return 'cake6_8';

    // Check 10 or quarter sheet keywords
    if (n === '10' || n.includes('quarter') || n.includes('1/4')) {

      // Return 10 or quarter rule key
      return 'cake10_quarter';

    }

    // Check word boundaries for 10 excluding sheet cakes
    if (n.match(/^10\b/) && !n.includes('sheet')) return 'cake10_quarter';

    // Check half or full sheet keywords
    if (n.includes('half') || n.includes('1/2') || n.includes('full')) {

      // Return half or full sheet rule key
      return 'cake_half_full';

    }

    // Check fallback exact match for 6 inch quotes
    if (n === '6"' || n === '6"') return 'cake6_8';

    // Check fallback exact match for 8 inch quotes
    if (n === '8"' || n === '8"') return 'cake6_8';

    // Check fallback exact match for 10 inch quotes
    if (n === '10"' || n === '10"') return 'cake10_quarter';

    // Check fallback exact match for 1/4 sheet
    if (n === '1/4 sheet' || n === 'quarter sheet') return 'cake10_quarter';

    // Check fallback exact match for 1/2 sheet
    if (n === '1/2 sheet' || n === 'half sheet') return 'cake_half_full';

    // Check fallback exact match for full sheet
    if (n === 'full sheet') return 'cake_half_full';

    // Return null if no cake size pattern matched
    return null;

  }

  /**
   * Classifies product family category based on product tags and type.
   *
   * @param {string[]|string} [tags=[]] - Product tags.
   * @param {string} [productType=""] - Product type string.
   * @returns {string|null} Family classification ("cake", "bakery", "drink") or null.
   */
  function getProductFamily(tags = [], productType = '') {

    // Convert tags to lowercase search string
    const tagStr = Array.isArray(tags) ? tags.join(' ').toLowerCase() : String(tags).toLowerCase();

    // Convert product type to lowercase string
    const typeStr = String(productType).toLowerCase();

    // Combine tags and product type into single searchable string
    const combined = tagStr + ' ' + typeStr;

    // Check for explicit drink or beverage classification
    if (tagStr.includes('type:drink') || tagStr.includes('type:beverage') || combined.includes('drink') || combined.includes('beverage')) {

      // Return drink family identifier
      return 'drink';

    }

    // Check for bakery classification
    if (tagStr.includes('type:bakery') || combined.includes('bakery')) {

      // Verify drink does not take precedence
      if (tagStr.includes('type:drink') || tagStr.includes('type:beverage')) return 'drink';

      // Return bakery family identifier
      return 'bakery';

    }

    // Check for explicit classic-cake tag or cake type
    if (tagStr.includes('type:classic-cake') || typeStr.includes('cake')) {

      // Return cake family identifier
      return 'cake';

    }

    // Fallback search for cake keyword
    if (combined.includes('cake')) return 'cake';

    // Fallback search for bakery keyword
    if (combined.includes('bakery')) return 'bakery';

    // Fallback search for drink or beverage keyword
    if (combined.includes('drink') || combined.includes('beverage')) return 'drink';

    // Return null if unclassified
    return null;

  }

  /**
   * Parses time string into elapsed minutes from midnight.
   *
   * @param {string} timeStr - Time string e.g. "08:30 AM".
   * @returns {number|null} Minutes from midnight or null.
   */
  function parseTimeToMinutes(timeStr) {

    // Return null if time string is empty
    if (!timeStr) return null;

    // Match 12-hour hours, minutes, and AM/PM format
    const m = String(timeStr).match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);

    // Return null if regex fails
    if (!m) return null;

    // Parse hours as integer
    let h = parseInt(m[1], 10);

    // Parse minutes as integer
    const min = parseInt(m[2], 10);

    // Extract uppercase meridiem indicator
    const ampm = m[3].toUpperCase();

    // Convert PM hours to 24-hour format
    if (ampm === 'PM' && h !== 12) h += 12;

    // Convert 12 AM to 0 hours
    if (ampm === 'AM' && h === 12) h = 0;

    // Calculate total minutes from midnight
    return h * 60 + min;

  }

  /**
   * Converts minutes from midnight into 12-hour formatted time string.
   *
   * @param {number} mins - Minutes from midnight.
   * @returns {string} Formatted time string e.g. "08:30 AM".
   */
  function minutesToTimeStr(mins) {

    // Calculate integer hours
    let h = Math.floor(mins / 60);

    // Calculate remainder minutes
    const m = mins % 60;

    // Determine AM or PM meridiem
    const ampm = h >= 12 ? 'PM' : 'AM';

    // Convert 0 hour to 12 AM
    if (h === 0) h = 12;

    // Convert 24-hour PM to 12-hour format
    else if (h > 12) h = h - 12;

    // Return zero-padded time string
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;

  }

  /**
   * Evaluates if a given date falls on Sunday.
   *
   * @param {Date} date - Date instance to test.
   * @returns {boolean} True if Sunday.
   */
  function isSunday(date) {

    // Evaluate day index against 0
    return date.getDay() === 0;

  }

  /**
   * Evaluates if a given date falls on Saturday.
   *
   * @param {Date} date - Date instance to test.
   * @returns {boolean} True if Saturday.
   */
  function isSaturday(date) {

    // Evaluate day index against 6
    return date.getDay() === 6;

  }

  /**
   * Returns a new Date instance advanced by N calendar days.
   *
   * @param {Date} date - Source date.
   * @param {number} n - Number of days to advance.
   * @returns {Date} New calculated date.
   */
  function addDays(date, n) {

    // Clone source date
    const d = new Date(date);

    // Advance calendar day
    d.setDate(d.getDate() + n);

    // Return advanced date
    return d;

  }

  /**
   * Normalizes a Date instance to midnight (00:00:00).
   *
   * @param {Date} date - Source date.
   * @returns {Date} Normalized date at midnight.
   */
  function startOfDay(date) {

    // Clone source date
    const d = new Date(date);

    // Reset hours, minutes, seconds, milliseconds to zero
    d.setHours(0, 0, 0, 0);

    // Return normalized date
    return d;

  }

  /**
   * Formats a Date instance into an ISO date string (YYYY-MM-DD).
   *
   * @param {Date} date - Date instance.
   * @returns {string} Formatted date key string.
   */
  function toDateKey(date) {

    // Extract four digit year
    const y = date.getFullYear();

    // Extract two digit zero-padded month
    const m = String(date.getMonth() + 1).padStart(2, '0');

    // Extract two digit zero-padded day
    const day = String(date.getDate()).padStart(2, '0');

    // Return joined ISO date key
    return `${y}-${m}-${day}`;

  }

  /**
   * Parses an ISO date string (YYYY-MM-DD) into a local Date instance.
   *
   * @param {string} value - ISO date string.
   * @returns {Date|null} Local Date instance or null.
   */
  function parseLocalDateISO(value) {

    // Return null if input is falsy
    if (!value) return null;

    // Split components into numbers
    const [year, month, day] = value.split('-').map(Number);

    // Verify all components exist
    if (!year || !month || !day) return null;

    // Return new local Date instance
    return new Date(year, month - 1, day);

  }

  /**
   * Retrieves candidate time slots for a specified date based on day of week.
   *
   * @param {Date} date - Target date.
   * @returns {string[]} Available time slots array copy.
   */
  function getSlotsForDay(date) {

    // Check if target date is Sunday
    if (isSunday(date)) {

      // Return copy of Sunday slots
      return DEFAULT_SLOTS_SUNDAY.slice();

    }

    // Return copy of Monday through Saturday slots
    return DEFAULT_SLOTS_MON_SAT.slice();

  }

  /**
   * Retrieves candidate 4:00 PM afternoon time slots.
   *
   * @returns {string[]} 4:00 PM slots array copy.
   */
  function getSlots4PM() {

    // Return copy of 4PM slots
    return SLOTS_4PM_MON_SAT.slice();

  }

  /**
   * Monday–Saturday Collection windows that start at 10:30 AM or later.
   *
   * @returns {string[]} Daytime slot list.
   */
  function getSlotsDaytime() {

    // Keep windows whose start is 10:30 AM or later
    return DEFAULT_SLOTS_MON_SAT.filter((slot) => {

      // Parse the window start
      const start = parseTimeToMinutes(slot);

      // 10:30 AM is 630 minutes
      return start != null && start >= 10 * 60 + 30;

    });

  }

  /**
   * Default store timezone identifier (Texas Central Time).
   * @constant {string}
   */
  const DEFAULT_STORE_TIMEZONE = 'America/Chicago';

  /**
   * Resolves current Date in a specified IANA timezone (defaults to America/Chicago for Texas).
   *
   * @param {string} [timezone='America/Chicago'] - Target IANA timezone.
   * @param {Date} [referenceDate=new Date()] - Reference date instance.
   * @returns {Date} Date instance representing wall-clock time in the target timezone.
   */
  function getNowInTimezone(timezone = DEFAULT_STORE_TIMEZONE, referenceDate = new Date()) {

    // Resolve target timezone
    const tz = timezone || DEFAULT_STORE_TIMEZONE;

    // Safely attempt timezone-aware date construction using native Intl API
    try {

      // Initialize date-time formatter configured for the target timezone
      const formatter = new Intl.DateTimeFormat('en-US', {

        // Specify target timezone
        timeZone: tz,

        // Format four digit year
        year: 'numeric',

        // Format two digit month
        month: '2-digit',

        // Format two digit day
        day: '2-digit',

        // Format two digit hour in 24-hour cycle
        hour: '2-digit',

        // Format two digit minute
        minute: '2-digit',

        // Format two digit second
        second: '2-digit',

        // Enforce 24-hour cycle
        hour12: false

      });

      // Format current timestamp into individual date parts
      const parts = formatter.formatToParts(referenceDate);

      // Initialize dictionary for extracted date components
      const map = {};

      // Iterate through formatted parts to populate component dictionary
      for (const p of parts) {

        // Store part value indexed by part type
        map[p.type] = p.value;

      }

      // Parse 24-hour integer value handling midnight boundary
      const parsedHour = parseInt(map.hour, 10);

      // Normalize 24 to 0 for midnight
      const hour = parsedHour === 24 ? 0 : parsedHour;

      // Construct and return Date representing the wall-clock time in target timezone
      return new Date(

        // Year component
        parseInt(map.year, 10),

        // Zero-indexed month component
        parseInt(map.month, 10) - 1,

        // Day of month component
        parseInt(map.day, 10),

        // Hour component
        hour,

        // Minute component
        parseInt(map.minute, 10),

        // Second component
        parseInt(map.second, 10)

      );

    // Fallback to reference date on environment errors
    } catch (err) {

      // Return reference date
      return referenceDate;

    }

  }

  /**
   * Bundled PickupConfig namespace object.
   * @constant {Object}
   */
  const PickupConfig = {

    // Default Mon-Sat slots
    DEFAULT_SLOTS_MON_SAT,

    // Default Sunday slots
    DEFAULT_SLOTS_SUNDAY,

    // 4PM Mon-Sat slots
    SLOTS_4PM_MON_SAT,

    // Weekday names
    DAYS,

    // Variant title normalizer
    normalizeVariantTitle,

    // Cake variant rule evaluator
    getVariantRule,

    // Product family evaluator
    getProductFamily,

    // Time to minutes parser
    parseTimeToMinutes,

    // Minutes to time string formatter
    minutesToTimeStr,

    // Sunday tester
    isSunday,

    // Saturday tester
    isSaturday,

    // Add days helper
    addDays,

    // Start of day helper
    startOfDay,

    // Date key formatter
    toDateKey,

    // Parse local date ISO
    parseLocalDateISO,

    // Slots for day helper
    getSlotsForDay,

    // Slots for 4PM helper
    getSlots4PM,

    // Default store timezone constant
    DEFAULT_STORE_TIMEZONE,

    // Timezone date resolver helper
    getNowInTimezone

  };

  /* ==========================================================================
     SECTION 2: PICKUP CONTEXT & VARIANT CHANGE MEDIATOR
     ========================================================================== */

  /**
   * Represents the single source of truth for product pickup state.
   * Manages product metadata, variant selections, active rules, dates, and times.
   *
   * @class PickupContext
   */
  class PickupContext {

    /**
     * Initializes a new instance of the PickupContext class.
     *
     * @constructor
     * @param {Object} [initialData={}] - Initial configuration and state payload.
     */
    constructor(initialData = {}) {

      // Store the product ID as a normalized string
      this.productId = String(initialData.productId || '');

      // Store the product type description
      this.productType = String(initialData.productType || '');

      // Normalize tags into an array of trimmed lowercase strings
      this.tags = Array.isArray(initialData.tags)
        ? initialData.tags
        : typeof initialData.tags === 'string'
        ? initialData.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : [];

      // Store the available product options
      this.options = Array.isArray(initialData.options) ? initialData.options : [];

      // Store the array of product variants
      this.variants = Array.isArray(initialData.variants) ? initialData.variants : [];

      // Store the currently active variant ID
      this.selectedVariantId = String(initialData.selectedVariantId || '');

      // Store the currently active variant title
      this.selectedVariantTitle = String(initialData.selectedVariantTitle || '');

      // Store the active geographical region code in lowercase
      this.region = String(initialData.region || 'us').toLowerCase();

      // Store the optional timezone identifier
      this.timezone = initialData.timezone || null;

      // Store the chosen pickup date string in YYYY-MM-DD format
      this.selectedDate = initialData.selectedDate || '';

      // Store the chosen pickup time slot window string
      this.selectedTime = initialData.selectedTime || '';

      // Store optional custom date provider function for testing and timezone evaluation
      this._nowProvider = typeof initialData.nowProvider === 'function' ? initialData.nowProvider : null;

      // Initialize collection of observer callback functions
      this._subscribers = new Set();

      // Initialize the product family classification
      this.productFamily = null;

      // Initialize the variant rule classification
      this.variantRule = null;

      // Compute derived domain fields such as productFamily and variantRule
      this._computeDerivedState();

    }

    /**
     * Resolves the current Date in the store's target timezone (Texas / Central Time).
     * Evaluates the custom provider if supplied, otherwise delegates to Intl timezone resolution.
     *
     * @method getNow
     * @returns {Date} The current Date instance in the store's target timezone.
     */
    getNow() {

      // Check if a custom date provider function was registered
      if (this._nowProvider) {

        // Return date from custom provider
        return this._nowProvider();

      }

      // Return timezone-converted date using Texas Central Time fallback
      return getNowInTimezone(this.timezone || DEFAULT_STORE_TIMEZONE);

    }

    /**
     * Recomputes derived domain fields based on tags, product type, and variant title.
     *
     * @private
     * @method _computeDerivedState
     * @returns {void}
     */
    _computeDerivedState() {

      // Evaluate product family using pure helper function
      this.productFamily = getProductFamily(this.tags, this.productType);

      // Check if variant title needs lookup from variants array
      if (!this.selectedVariantTitle && this.selectedVariantId && this.variants.length > 0) {

        // Search for matching variant by identifier
        const matched = this.variants.find((v) => String(v.id) === String(this.selectedVariantId));

        // If a matching variant is located
        if (matched) {

          // Check if variant has a valid title other than Default Title
          if (matched.title && matched.title !== 'Default Title') {

            // Assign title from matched variant
            this.selectedVariantTitle = matched.title;

          // Check if options array is present
          } else if (Array.isArray(matched.options) && matched.options.length > 0) {

            // Join option values into title string
            this.selectedVariantTitle = matched.options.join(' / ');

          }

        }

      }

      // Evaluate rule using variant title helper
      this.variantRule = getVariantRule(this.selectedVariantTitle);

    }

    /**
     * Updates the variant state and recalculates derived rule properties.
     * Triggers notifications to all registered observers if variant changed.
     *
     * @method updateVariant
     * @param {string|number} variantId - The new variant identifier.
     * @param {string} [variantTitle=''] - The new variant title or size string.
     * @returns {boolean} True if state changed; otherwise false.
     */
    updateVariant(variantId, variantTitle = '') {

      // Normalize variant identifier to string
      const normalizedId = String(variantId || '');

      // Capture previous variant identifier for transition tracking
      const prevId = this.selectedVariantId;

      // Capture previous variant title
      const prevTitle = this.selectedVariantTitle;

      // Initialize resolved title variable
      let resolvedTitle = variantTitle;

      // If title is missing and variants array is populated
      if (!resolvedTitle && this.variants.length > 0) {

        // Find the variant item in the list
        const found = this.variants.find((v) => String(v.id) === normalizedId);

        // If found, resolve readable title
        if (found) {

          // Extract title or options
          resolvedTitle = (found.title && found.title !== 'Default Title') ? found.title : (found.options || []).join(' / ');

        }

      }

      // Check if variant ID and title are identical to current values
      if (normalizedId === prevId && (!resolvedTitle || resolvedTitle === prevTitle)) {

        // Return false indicating no state modification occurred
        return false;

      }

      // Assign the new variant identifier
      this.selectedVariantId = normalizedId;

      // Assign the new variant title if resolved
      if (resolvedTitle) {

        // Update variant title property
        this.selectedVariantTitle = resolvedTitle;

      }

      // Recompute family and variant rule classifications
      this._computeDerivedState();

      // Notify all registered subscribers of the variant change event
      this.notify({ type: 'variant_change', previous: { variantId: prevId, variantTitle: prevTitle } });

      // Return true indicating successful state update
      return true;

    }

    /**
     * Updates the selected pickup date.
     *
     * @method updateDate
     * @param {string} date - Date string in ISO YYYY-MM-DD format.
     * @returns {void}
     */
    updateDate(date) {

      // Store reference to previous date
      const prevDate = this.selectedDate;

      // Normalize and set new selected date
      this.selectedDate = String(date || '').trim();

      // Check if value changed
      if (prevDate !== this.selectedDate) {

        // Notify observers of date change
        this.notify({ type: 'date_change', previous: { date: prevDate } });

      }

    }

    /**
     * Updates the selected pickup time window.
     *
     * @method updateTime
     * @param {string} time - Time slot window string.
     * @returns {void}
     */
    updateTime(time) {

      // Store reference to previous time
      const prevTime = this.selectedTime;

      // Normalize and set new selected time
      this.selectedTime = String(time || '').trim();

      // Check if value changed
      if (prevTime !== this.selectedTime) {

        // Notify observers of time change
        this.notify({ type: 'time_change', previous: { time: prevTime } });

      }

    }

    /**
     * Resets both selected date and time.
     *
     * @method resetSelection
     * @returns {void}
     */
    resetSelection() {

      // Determine if a selection was previously active
      const hadSelection = Boolean(this.selectedDate || this.selectedTime);

      // Clear the selected date
      this.selectedDate = '';

      // Clear the selected time
      this.selectedTime = '';

      // If active selection was cleared
      if (hadSelection) {

        // Notify observers of reset event
        this.notify({ type: 'selection_reset' });

      }

    }

    /**
     * Subscribes an observer callback to context change events.
     *
     * @method subscribe
     * @param {Function} listener - Callback function accepting (event, context).
     * @returns {Function} Unsubscribe teardown function.
     */
    subscribe(listener) {

      // Validate listener is a callable function
      if (typeof listener !== 'function') return () => {};

      // Add listener to subscriber set
      this._subscribers.add(listener);

      // Return unsubscribe closure
      return () => {

        // Remove listener from set
        this._subscribers.delete(listener);

      };

    }

    /**
     * Broadcasts an event payload to all registered subscriber functions.
     *
     * @method notify
     * @param {Object} [event={}] - Event details object.
     * @returns {void}
     */
    notify(event = {}) {

      // Construct immutable payload containing context reference
      const payload = { ...event, context: this };

      // Iterate through each registered subscriber
      for (const listener of this._subscribers) {

        // Wrap execution in try-catch to prevent observer errors from breaking the caller
        try {

          // Invoke subscriber callback
          listener(payload, this);

        // Catch and log callback failure
        } catch (err) {

          // Log error to console
          console.error('[PickupContext] Subscriber error:', err);

        }

      }

    }

    /**
     * Produces a clean JSON serializable representation of current state.
     *
     * @method toJSON
     * @returns {Object} Plain object snapshot.
     */
    toJSON() {

      // Return state snapshot object
      return {

        // Include product ID
        productId: this.productId,

        // Include product type
        productType: this.productType,

        // Include tags array copy
        tags: this.tags.slice(),

        // Include product family
        productFamily: this.productFamily,

        // Include selected variant ID
        selectedVariantId: this.selectedVariantId,

        // Include selected variant title
        selectedVariantTitle: this.selectedVariantTitle,

        // Include variant rule key
        variantRule: this.variantRule,

        // Include selected date
        selectedDate: this.selectedDate,

        // Include selected time
        selectedTime: this.selectedTime,

        // Include region
        region: this.region,

        // Include timezone
        timezone: this.timezone

      };

    }

  }

  /**
   * Mediator that observes theme variant changes, DOM events, and forms,
   * cleanly updating the PickupContext without needing page reloads.
   *
   * @class VariantChangeMediator
   */
  class VariantChangeMediator {

    /**
     * Initializes a new instance of the VariantChangeMediator class.
     *
     * @constructor
     * @param {PickupContext} context - The context instance to update.
     * @param {HTMLElement} rootElement - The host DOM element.
     */
    constructor(context, rootElement) {

      // Store reference to context instance
      this.context = context;

      // Store reference to host DOM element
      this.rootElement = rootElement;

      // List of teardown unsubscribe functions
      this._unsubscribers = [];

      // Bind form change handler to preserve scope
      this._boundOnFormChange = this._onFormChange.bind(this);

      // Bind custom event handler to preserve scope
      this._boundOnCustomEvent = this._onCustomEvent.bind(this);

      // Bind history popstate handler to preserve scope
      this._boundOnPopState = this._onPopState.bind(this);

    }

    /**
     * Binds all relevant listeners and theme subscriptions.
     *
     * @method mount
     * @returns {void}
     */
    mount() {

      // Check if theme store subscription is accessible
      if (typeof window !== 'undefined' && typeof window.cartFormSubscribe === 'function') {

        // Attempt cartForm subscription
        try {

          // Subscribe to theme cart form store changes
          const unsubscribeCartForm = window.cartFormSubscribe(() => {

            // Re-evaluate variant from theme store
            this._checkAndUpdateFromTheme();

          });

          // If unsubscribe function was returned
          if (typeof unsubscribeCartForm === 'function') {

            // Store unsubscribe callback
            this._unsubscribers.push(unsubscribeCartForm);

          }

        // Catch subscription error gracefully
        } catch (e) {

          // Log warning
          console.warn('[VariantChangeMediator] cartFormSubscribe error:', e);

        }

      }

      // List of custom variant change events fired across Shopify themes
      const customEvents = ['variant:change', 'product:variant-change', 'xo:variant:change', 'variant:change:pickup'];

      // Attach listener for each event type
      customEvents.forEach((evtName) => {

        // Add document-level event listener
        document.addEventListener(evtName, this._boundOnCustomEvent);

        // Register teardown function
        this._unsubscribers.push(() => document.removeEventListener(evtName, this._boundOnCustomEvent));

      });

      // Locate parent or associated product form
      const form = this._findProductForm();

      // If form element exists
      if (form) {

        // Add change event listener to form
        form.addEventListener('change', this._boundOnFormChange);

        // Register teardown function
        this._unsubscribers.push(() => form.removeEventListener('change', this._boundOnFormChange));

      }

      // Listen for browser history back/forward navigation
      if (typeof window !== 'undefined') {

        // Add popstate listener
        window.addEventListener('popstate', this._boundOnPopState);

        // Register teardown function
        this._unsubscribers.push(() => window.removeEventListener('popstate', this._boundOnPopState));

      }

    }

    /**
     * Unmounts all registered listeners and clears resources.
     *
     * @method unmount
     * @returns {void}
     */
    unmount() {

      // Drain all registered unsubscription callbacks
      while (this._unsubscribers.length > 0) {

        // Pop last teardown function
        const unsub = this._unsubscribers.pop();

        // Safely execute teardown
        try {

          // Invoke teardown closure
          unsub();

        // Suppress teardown errors
        } catch (e) {}

      }

    }

    /**
     * Locates the related product form element.
     *
     * @private
     * @method _findProductForm
     * @returns {HTMLFormElement|null} Found form element or null.
     */
    _findProductForm() {

      // Validate host element exists
      if (!this.rootElement) return null;

      // Find closest standard cart add form
      return (

        // Check for closest add-to-cart form
        this.rootElement.closest('form[action*="/cart/add"]') ||

        // Check inside closest xo-product wrapper
        this.rootElement.closest('xo-product')?.querySelector('form[action*="/cart/add"]') ||

        // Check globally for form with product ID
        document.querySelector(`form[action*="/cart/add"][data-product-id="${this.context.productId}"]`)

      );

    }

    /**
     * Resolves the current variant ID from DOM inputs or URL parameters.
     *
     * @private
     * @method _resolveCurrentVariantId
     * @returns {string} The active variant ID.
     */
    _resolveCurrentVariantId() {

      // Locate product form
      const form = this._findProductForm();

      // Check form controls
      if (form) {

        // Locate variant ID input or select
        const idInput = form.querySelector('input[name="id"], select[name="id"]');

        // Check if value is populated
        if (idInput && idInput.value) {

          // Return stringified variant ID
          return String(idInput.value);

        }

      }

      // Check URL query parameters as secondary source
      try {

        // Parse current window URL
        const url = new URL(window.location.href);

        // Read variant query parameter
        const variantParam = url.searchParams.get('variant');

        // If parameter exists, return it
        if (variantParam) return String(variantParam);

      // Suppress URL parsing errors
      } catch (e) {}

      // Fallback to existing context variant ID
      return this.context.selectedVariantId || '';

    }

    /**
     * Resolves the currently selected variant title (e.g. Size radio/select value).
     *
     * @private
     * @method _resolveCurrentVariantTitle
     * @returns {string} The resolved variant title.
     */
    _resolveCurrentVariantTitle() {

      // Locate product form
      const form = this._findProductForm();

      // Check form controls
      if (form) {

        // Query for checked size or option radios
        const sizeRadios = form.querySelectorAll(
          'input[type=radio][name^="options["]:checked, input[type=radio][name*="Size"]:checked, input[type=radio][name*="size"]:checked'
        );

        // Iterate through matching checked radio buttons
        for (const radio of sizeRadios) {

          // If radio has a non-empty value, return it
          if (radio.value) return radio.value;

        }

        // Query for option select elements
        const selects = form.querySelectorAll('select[name^="options["]');

        // Iterate through option dropdowns
        for (const select of selects) {

          // If dropdown has a non-empty value, return it
          if (select.value) return select.value;

        }

      }

      // Return empty string fallback
      return '';

    }

    /**
     * Reads variant state from the theme cartForm reactive store.
     *
     * @private
     * @method _checkAndUpdateFromTheme
     * @returns {void}
     */
    _checkAndUpdateFromTheme() {

      // Verify theme state getter is defined
      if (typeof window.getCartFormState !== 'function') return;

      // Execute safely
      try {

        // Find enclosing xo-product component
        const productEl = this.rootElement.closest('xo-product');

        // If product wrapper is missing, exit
        if (!productEl) return;

        // Retrieve section ID attribute
        const sectionId = productEl.getAttribute('xo-section-id');

        // Retrieve product ID attribute
        const productId = productEl.getAttribute('xo-product-id');

        // Build theme store lookup key
        const key = `${sectionId}/${productId}`;

        // Retrieve theme state for this product
        const state = window.getCartFormState()?.[key];

        // Check if variant selection exists in state
        if (state && state.variantSelected) {

          // Extract option values array
          const values = Object.values(state.variantSelected);

          // Join option values into title
          const title = values.join(' / ');

          // Resolve variant identifier
          const varId = this._resolveCurrentVariantId();

          // Dispatch update to context
          this._dispatchVariantUpdate(varId, title);

        }

      // Catch and ignore theme read errors
      } catch (e) {}

    }

    /**
     * Handles change events on form inputs.
     *
     * @private
     * @method _onFormChange
     * @param {Event} event - The DOM change event.
     * @returns {void}
     */
    _onFormChange(event) {

      // Extract event target
      const target = event.target;

      // Validate target exists
      if (!target) return;

      // Extract input name attribute
      const name = target.name || '';

      // Determine if changed input pertains to variant or size selection
      const isVariantField =
        name === 'id' ||
        name.includes('options[') ||
        name.toLowerCase().includes('size') ||
        target.type === 'radio';

      // If input relates to variants
      if (isVariantField) {

        // Debounce slightly to allow theme scripts to complete DOM updates
        setTimeout(() => {

          // Resolve variant ID
          const varId = this._resolveCurrentVariantId();

          // Resolve variant title
          const varTitle = this._resolveCurrentVariantTitle();

          // Dispatch update
          this._dispatchVariantUpdate(varId, varTitle);

        }, 20);

      }

    }

    /**
     * Handles custom DOM variant change events.
     *
     * @private
     * @method _onCustomEvent
     * @param {CustomEvent} event - Custom variant event.
     * @returns {void}
     */
    _onCustomEvent(event) {

      // Allow microtask tick for DOM settlement
      setTimeout(() => {

        // Extract detail payload
        const detail = event.detail || {};

        // Resolve variant ID from detail or DOM
        const varId = detail.variantId || detail.id || this._resolveCurrentVariantId();

        // Resolve variant title from detail or DOM
        const varTitle = detail.variantTitle || detail.title || this._resolveCurrentVariantTitle();

        // Dispatch update
        this._dispatchVariantUpdate(varId, varTitle);

      }, 20);

    }

    /**
     * Handles browser popstate navigation.
     *
     * @private
     * @method _onPopState
     * @returns {void}
     */
    _onPopState() {

      // Resolve variant ID from URL/DOM
      const varId = this._resolveCurrentVariantId();

      // Resolve variant title from DOM
      const varTitle = this._resolveCurrentVariantTitle();

      // Dispatch update
      this._dispatchVariantUpdate(varId, varTitle);

    }

    /**
     * Passes variant update to context if identifier or title exists.
     *
     * @private
     * @method _dispatchVariantUpdate
     * @param {string} variantId - Variant ID.
     * @param {string} variantTitle - Variant title.
     * @returns {void}
     */
    _dispatchVariantUpdate(variantId, variantTitle) {

      // Validate at least one property exists
      if (!variantId && !variantTitle) return;

      // Update variant on context
      this.context.updateVariant(variantId, variantTitle);

    }

  }

  /* ==========================================================================
     SECTION 3: PICKUP RULES (STRATEGY PATTERN)
     ========================================================================== */

  /**
   * Base abstract class defining the contract for all pickup rules.
   * Follows the Strategy Pattern to decouple rule logic from DOM and components.
   *
   * @abstract
   * @class AbstractPickupRule
   */
  class AbstractPickupRule {

    /**
     * Initializes rule metadata.
     *
     * @constructor
     * @param {string} name - The descriptive rule name identifier.
     */
    constructor(name) {

      // Assign descriptive rule name
      this.name = name;

    }

    /**
     * Determines whether this rule applies to the given context.
     *
     * @abstract
     * @method isApplicable
     * @param {Object} context - The current domain context.
     * @returns {boolean} True if applicable.
     */
    isApplicable(context) {

      // Throw error if method is not implemented by concrete subclass
      throw new Error(`[${this.name}] isApplicable() must be implemented.`);

    }

    /**
     * Calculates the earliest selectable pickup date.
     *
     * @abstract
     * @method getMinDate
     * @param {Object} context - The current domain context.
     * @param {Date} now - The reference current date.
     * @returns {Date} The minimum allowed pickup date.
     */
    getMinDate(context, now) {

      // Throw error if method is not implemented by concrete subclass
      throw new Error(`[${this.name}] getMinDate() must be implemented.`);

    }

    /**
     * Retrieves the raw list of candidate time windows for a specific date.
     *
     * @abstract
     * @method getWindowsForDate
     * @param {Date} date - The date to inspect.
     * @param {Object} context - The current domain context.
     * @param {Date} now - The reference current date.
     * @returns {string[]} Raw list of time slot strings.
     */
    getWindowsForDate(date, context, now) {

      // Throw error if method is not implemented by concrete subclass
      throw new Error(`[${this.name}] getWindowsForDate() must be implemented.`);

    }

    /**
     * Filters time slots based on cutoff rules and elapsed time.
     *
     * @method filterSlots
     * @param {string[]} slots - Raw time slots list.
     * @param {Date} date - Target date.
     * @param {Object} context - Current domain context.
     * @param {Date} now - Reference current date.
     * @returns {string[]} Filtered available slots.
     */
    filterSlots(slots, date, context, now) {

      // Normalize date to midnight
      const targetDay = startOfDay(date);

      // Normalize current date to midnight
      const currentDay = startOfDay(now);

      // Determine if target date is today
      const isToday = toDateKey(targetDay) === toDateKey(currentDay);

      // If not today, no past-slot filtering is needed
      if (!isToday) {

        // Return untouched slots copy
        return slots.slice();

      }

      // Calculate current minutes from midnight
      const nowMins = now.getHours() * 60 + now.getMinutes();

      // Filter slots that have already started or passed
      return slots.filter((slot) => {

        // Parse slot start minutes
        const startMins = parseTimeToMinutes(slot);

        // Discard invalid slot format
        if (startMins == null) return false;

        // Keep slot only if it starts at or after current time
        return startMins >= nowMins;

      });

    }

    /**
     * Evaluates final allowed time slots for a given date.
     *
     * @method windowsFor
     * @param {Date} date - Target date.
     * @param {Object} context - Domain context.
     * @param {Date} now - Reference current date.
     * @returns {string[]} Final filtered available slots.
     */
    windowsFor(date, context, now) {

      // Get raw candidate windows for date
      const candidateSlots = this.getWindowsForDate(date, context, now);

      // Apply cutoff and past-time filtering
      return this.filterSlots(candidateSlots, date, context, now);

    }

    /**
     * Checks if a specific date is allowed for pickup.
     *
     * @method isAllowed
     * @param {Date} date - Target date.
     * @param {Object} context - Domain context.
     * @param {Date} now - Reference current date.
     * @returns {boolean} True if pickup is possible on this date.
     */
    isAllowed(date, context, now) {

      // Normalize date
      const d = startOfDay(date);

      // Calculate minimum allowed date
      const minDate = startOfDay(this.getMinDate(context, now));

      // Reject date if earlier than minimum date
      if (d < minDate) return false;

      // Calculate slots available on this date
      const availableSlots = this.windowsFor(d, context, now);

      // Date is allowed only if at least one slot is open
      return availableSlots.length > 0;

    }

    /**
     * Calendar-day notice required before a Pickup day, or 0 when notice is not the rule.
     *
     * @returns {number} Notice in calendar days.
     */
    noticeDays() {

      // Most rules are cutoffs, not a fixed notice
      return 0;

    }

    /**
     * Why a Pickup day cannot be collected, or null when it can.
     *
     * @param {Date} date - Target date.
     * @param {Object} context - Domain context.
     * @param {Date} now - Reference current date.
     * @returns {{ code: string, earliest: Date, days?: number }|null} Rejection, or null.
     */
    rejectionReason(date, context, now) {

      // Normalize the chosen day
      const d = startOfDay(date);

      // Earliest Pickup day for this order
      const earliest = startOfDay(this.getMinDate(context, now));

      // Chosen day is before the earliest day
      if (d < earliest) {

        // Fixed notice uses the notice sentence
        const days = this.noticeDays();

        // 2-day and 4-day cakes
        if (days > 0) {

          // Notice rejection
          return { code: 'notice', days, earliest };

        }

        // Same-day cutoff or a finished today
        return { code: 'too_soon', earliest };

      }

      // A day on or after the earliest day with no bookable window
      if (this.windowsFor(d, context, now).length === 0) {

        // Closed day
        return { code: 'closed', earliest };

      }

      // The day can be collected
      return null;

    }

    /**
     * Finds the next earliest valid date and slot list starting from a given date.
     *
     * @method autoAdvance
     * @param {Date} date - Starting candidate date.
     * @param {Object} context - Domain context.
     * @param {Date} now - Reference current date.
     * @returns {{ date: Date, slots: string[] }} Nearest valid date and its slots.
     */
    autoAdvance(date, context, now) {

      // Start candidate at midnight
      let candidate = startOfDay(date);

      // Calculate minimum date
      const minDate = startOfDay(this.getMinDate(context, now));

      // Ensure candidate starts at or after minimum date
      if (candidate < minDate) {

        // Advance candidate to minimum date
        candidate = minDate;

      }

      // Search up to 60 calendar days ahead
      for (let i = 0; i < 60; i++) {

        // Compute current search date
        const checkDate = addDays(candidate, i);

        // Check if date satisfies all rule criteria
        if (this.isAllowed(checkDate, context, now)) {

          // Retrieve open slots
          const openSlots = this.windowsFor(checkDate, context, now);

          // If slots exist, return this date
          if (openSlots.length > 0) {

            // Return matching date and slots
            return { date: checkDate, slots: openSlots };

          }

        }

      }

      // Fallback to initial candidate
      return { date: candidate, slots: this.windowsFor(candidate, context, now) };

    }

  }

  /**
   * Concrete Strategy: Cake 6" and 8" Pickup Rule.
   *
   * @class Cake6To8Rule
   * @extends AbstractPickupRule
   */
  class Cake6To8Rule extends AbstractPickupRule {

    /**
     * Initializes the Cake 6"/8" rule strategy.
     *
     * @constructor
     */
    constructor() {

      // Call super with identifier
      super('Cake6To8Rule');

    }

    /**
     * Applies to classic cakes with size 6" or 8".
     *
     * @override
     * @method isApplicable
     * @param {Object} context - Domain context.
     * @returns {boolean}
     */
    isApplicable(context) {

      // Check for cake family and cake6_8 variant rule
      return context.productFamily === 'cake' && context.variantRule === 'cake6_8';

    }

    /**
     * Calculates the minimum selectable date for 6"/8" cakes.
     *
     * @override
     * @method getMinDate
     * @param {Object} context - Domain context.
     * @param {Date} now - Reference current date.
     * @returns {Date} Minimum date.
     */
    getMinDate(context, now) {

      // Normalize today to midnight
      const today = startOfDay(now);

      // Same-day Sunday is never a Pickup day for these cakes
      if (isSunday(today)) {

        // Earliest day is Monday
        return startOfDay(addDays(today, 1));

      }

      // Calculate minutes elapsed today
      const nowMins = now.getHours() * 60 + now.getMinutes();

      // Check if current time is before 12:00 PM noon cutoff
      if (nowMins < 12 * 60) {

        // Check available slots today
        const slotsToday = this.windowsFor(today, context, now);

        // If at least one 4PM slot remains today
        if (slotsToday.length > 0) {

          // Same-day pickup allowed
          return today;

        }

      }

      // Next calendar day, including Sunday
      return startOfDay(addDays(today, 1));

    }

    /**
     * Sunday uses Sunday windows. Same-day before noon uses 4:00 PM windows. Later days use daytime windows.
     *
     * @override
     * @method getWindowsForDate
     * @param {Date} date - Target date.
     * @param {Object} context - Domain context.
     * @param {Date} now - Reference current date.
     * @returns {string[]} Slot list.
     */
    getWindowsForDate(date, context, now) {

      // Sunday Collection windows, including a future Sunday
      if (isSunday(date)) {

        // Return the Sunday list
        return getSlotsForDay(date);

      }

      // Same calendar day as the order, before noon, uses the 4:00 PM list
      if (now && toDateKey(startOfDay(date)) === toDateKey(startOfDay(now))) {

        // Minutes since midnight
        const nowMins = now.getHours() * 60 + now.getMinutes();

        // Before noon
        if (nowMins < 12 * 60) {

          // Same-day afternoon windows
          return getSlots4PM();

        }

      }

      // Later Monday–Saturday, or today after the noon cutoff
      return getSlotsDaytime();

    }

  }

  /**
   * Concrete Strategy: Cake 10" and 1/4 Sheet Pickup Rule.
   *
   * @class Cake10QuarterRule
   * @extends AbstractPickupRule
   */
  class Cake10QuarterRule extends AbstractPickupRule {

    /**
     * Initializes the Cake 10"/Quarter Sheet rule strategy.
     *
     * @constructor
     */
    constructor() {

      // Call super with identifier
      super('Cake10QuarterRule');

    }

    /**
     * Applies to classic cakes with 10" or 1/4 sheet variant.
     *
     * @override
     * @method isApplicable
     * @param {Object} context - Domain context.
     * @returns {boolean}
     */
    isApplicable(context) {

      // Match cake family and cake10_quarter rule
      return context.productFamily === 'cake' && context.variantRule === 'cake10_quarter';

    }

    /**
     * Earliest pickup is today + 2 calendar days.
     *
     * @override
     * @method getMinDate
     * @param {Object} context - Domain context.
     * @param {Date} now - Reference current date.
     * @returns {Date} Minimum date.
     */
    getMinDate(context, now) {

      // Advance 2 calendar days from now
      return startOfDay(addDays(now, 2));

    }

    /**
     * Two calendar days of notice.
     *
     * @override
     * @returns {number} Notice days.
     */
    noticeDays() {

      // Fixed notice
      return 2;

    }

    /**
     * Sunday uses Sunday windows. Monday–Saturday uses daytime windows.
     *
     * @override
     * @method getWindowsForDate
     * @param {Date} date - Target date.
     * @returns {string[]} Slot list.
     */
    getWindowsForDate(date) {

      // Sunday Collection windows
      if (isSunday(date)) {

        // Return the Sunday list
        return getSlotsForDay(date);

      }

      // Daytime Monday–Saturday windows
      return getSlotsDaytime();

    }

  }

  /**
   * Concrete Strategy: Cake 1/2 Sheet and Full Sheet Pickup Rule.
   *
   * @class CakeHalfFullRule
   * @extends AbstractPickupRule
   */
  class CakeHalfFullRule extends AbstractPickupRule {

    /**
     * Initializes the Cake 1/2 and Full Sheet rule strategy.
     *
     * @constructor
     */
    constructor() {

      // Call super with identifier
      super('CakeHalfFullRule');

    }

    /**
     * Applies to classic cakes with 1/2 sheet or full sheet variant.
     *
     * @override
     * @method isApplicable
     * @param {Object} context - Domain context.
     * @returns {boolean}
     */
    isApplicable(context) {

      // Match cake family and cake_half_full rule
      return context.productFamily === 'cake' && context.variantRule === 'cake_half_full';

    }

    /**
     * Earliest pickup is today + 4 calendar days.
     *
     * @override
     * @method getMinDate
     * @param {Object} context - Domain context.
     * @param {Date} now - Reference current date.
     * @returns {Date} Minimum date.
     */
    getMinDate(context, now) {

      // Advance 4 calendar days from now
      return startOfDay(addDays(now, 4));

    }

    /**
     * Four calendar days of notice.
     *
     * @override
     * @returns {number} Notice days.
     */
    noticeDays() {

      // Fixed notice
      return 4;

    }

    /**
     * Sunday uses Sunday windows. Monday–Saturday uses daytime windows.
     *
     * @override
     * @method getWindowsForDate
     * @param {Date} date - Target date.
     * @returns {string[]} Slot list.
     */
    getWindowsForDate(date) {

      // Sunday Collection windows
      if (isSunday(date)) {

        // Return the Sunday list
        return getSlotsForDay(date);

      }

      // Daytime Monday–Saturday windows
      return getSlotsDaytime();

    }

  }

  /**
   * Concrete Strategy: Bakery Pickup Rule.
   *
   * @class BakeryRule
   * @extends AbstractPickupRule
   */
  class BakeryRule extends AbstractPickupRule {

    /**
     * Initializes the Bakery rule strategy.
     *
     * @constructor
     */
    constructor() {

      // Call super with identifier
      super('BakeryRule');

    }

    /**
     * Applies to items in the bakery product family.
     *
     * @override
     * @method isApplicable
     * @param {Object} context - Domain context.
     * @returns {boolean}
     */
    isApplicable(context) {

      // Match bakery product family
      return context.productFamily === 'bakery';

    }

    /**
     * Calculates minimum date for bakery items.
     *
     * @override
     * @method getMinDate
     * @param {Object} context - Domain context.
     * @param {Date} now - Reference current date.
     * @returns {Date} Minimum date.
     */
    getMinDate(context, now) {

      // Normalize today to midnight
      const today = startOfDay(now);

      // Windows that clear the 30-minute buffer
      const slotsToday = this.windowsFor(today, context, now);

      // Today stays open when one window remains
      if (slotsToday.length > 0) {

        // Return today
        return today;

      }

      // Otherwise the next calendar day
      return startOfDay(addDays(today, 1));

    }

    /**
     * Sunday list on Sunday, full weekday list otherwise.
     *
     * @override
     * @method getWindowsForDate
     * @param {Date} date - Target date.
     * @returns {string[]} Slot list.
     */
    getWindowsForDate(date) {

      // Same day list as drinks
      return getSlotsForDay(date);

    }

    /**
     * Drops same-day windows that start within 30 minutes, matching drinks.
     *
     * @override
     * @method filterSlots
     * @param {string[]} slots - Raw candidate slots.
     * @param {Date} date - Target date.
     * @param {Object} context - Domain context.
     * @param {Date} now - Reference current date.
     * @returns {string[]} Filtered slot list.
     */
    filterSlots(slots, date, context, now) {

      // Normalize the target day
      const targetDay = startOfDay(date);

      // Normalize today
      const currentDay = startOfDay(now);

      // A future day has no buffer
      if (toDateKey(targetDay) !== toDateKey(currentDay)) {

        // Return the day's full list
        return slots.slice();

      }

      // Start must be at least 30 minutes from now
      const cutoffMinutes = now.getHours() * 60 + now.getMinutes() + 30;

      // Keep windows that clear the buffer
      return slots.filter((slot) => {

        // Parse the window start
        const startMins = parseTimeToMinutes(slot);

        // Discard a malformed window
        if (startMins == null) return false;

        // Exactly 30 minutes away is bookable
        return startMins >= cutoffMinutes;

      });

    }

  }

  /**
   * Concrete Strategy: Drinks and Beverages Pickup Rule.
   *
   * @class DrinkRule
   * @extends AbstractPickupRule
   */
  class DrinkRule extends AbstractPickupRule {

    /**
     * Initializes the Drink rule strategy.
     *
     * @constructor
     */
    constructor() {

      // Call super with identifier
      super('DrinkRule');

    }

    /**
     * Applies to drink and beverage items.
     *
     * @override
     * @method isApplicable
     * @param {Object} context - Domain context.
     * @returns {boolean}
     */
    isApplicable(context) {

      // Match drink product family
      return context.productFamily === 'drink';

    }

    /**
     * Calculates minimum date for drinks.
     *
     * @override
     * @method getMinDate
     * @param {Object} context - Domain context.
     * @param {Date} now - Reference current date.
     * @returns {Date} Minimum date.
     */
    getMinDate(context, now) {

      // Normalize today to midnight
      const today = startOfDay(now);

      // Evaluate slots available today with 30-minute buffer
      const slotsToday = this.windowsFor(today, context, now);

      // If at least one slot is >= now + 30 min
      if (slotsToday.length > 0) {

        // Return today
        return today;

      }

      // Otherwise advance to next day
      return startOfDay(addDays(today, 1));

    }

    /**
     * Candidate slots are standard day slots.
     *
     * @override
     * @method getWindowsForDate
     * @param {Date} date - Target date.
     * @returns {string[]} Slot list.
     */
    getWindowsForDate(date) {

      // Return default slots for day
      return getSlotsForDay(date);

    }

    /**
     * Filters slots requiring start time >= now + 30 minutes on current day.
     *
     * @override
     * @method filterSlots
     * @param {string[]} slots - Raw candidate slots.
     * @param {Date} date - Target date.
     * @param {Object} context - Domain context.
     * @param {Date} now - Reference current date.
     * @returns {string[]} Filtered slot list.
     */
    filterSlots(slots, date, context, now) {

      // Normalize date to midnight
      const targetDay = startOfDay(date);

      // Normalize current date
      const currentDay = startOfDay(now);

      // Check if target is today
      const isToday = toDateKey(targetDay) === toDateKey(currentDay);

      // If not today, no time buffer is needed
      if (!isToday) {

        // Return slots copy
        return slots.slice();

      }

      // Calculate cutoff minutes (now + 30 minutes preparation buffer)
      const cutoffMinutes = now.getHours() * 60 + now.getMinutes() + 30;

      // Filter slots starting at or after cutoff
      return slots.filter((slot) => {

        // Parse start minutes of slot
        const startMins = parseTimeToMinutes(slot);

        // Discard invalid slot string
        if (startMins == null) return false;

        // Keep slot only if it meets the 30-minute buffer
        return startMins >= cutoffMinutes;

      });

    }

  }

  /**
   * Concrete Strategy: Default Fallback Pickup Rule.
   *
   * @class DefaultPickupRule
   * @extends AbstractPickupRule
   */
  class DefaultPickupRule extends AbstractPickupRule {

    /**
     * Initializes the default pickup rule strategy.
     *
     * @constructor
     */
    constructor() {

      // Call super with identifier
      super('DefaultPickupRule');

    }

    /**
     * Always applicable as final fallback.
     *
     * @override
     * @method isApplicable
     * @returns {boolean} True.
     */
    isApplicable() {

      // Default strategy matches everything
      return true;

    }

    /**
     * Calculates minimum date.
     *
     * @override
     * @method getMinDate
     * @param {Object} context - Domain context.
     * @param {Date} now - Reference current date.
     * @returns {Date} Minimum date.
     */
    getMinDate(context, now) {

      // Start at today midnight
      return startOfDay(now);

    }

    /**
     * Candidate slots are default for day.
     *
     * @override
     * @method getWindowsForDate
     * @param {Date} date - Target date.
     * @returns {string[]} Slot list.
     */
    getWindowsForDate(date) {

      // Return default slots for day
      return getSlotsForDay(date);

    }

  }

  /**
   * Bundled PickupRules namespace object.
   * @constant {Object}
   */
  const PickupRules = {

    // Base abstract class
    AbstractPickupRule,

    // Cake 6"/8" rule
    Cake6To8Rule,

    // Cake 10"/Quarter sheet rule
    Cake10QuarterRule,

    // Cake 1/2 and full sheet rule
    CakeHalfFullRule,

    // Bakery rule
    BakeryRule,

    // Drink rule
    DrinkRule,

    // Fallback default rule
    DefaultPickupRule

  };

  /* ==========================================================================
     SECTION 4: PICKUP RULE FACTORY (FACTORY PATTERN)
     ========================================================================== */

  /**
   * Factory class that manages rule registration and resolves the active
   * strategy for any given PickupContext.
   *
   * @class PickupRuleFactory
   */
  class PickupRuleFactory {

    /**
     * Initializes a new instance of the PickupRuleFactory.
     *
     * @constructor
     */
    constructor() {

      // Array holding registered rule strategy instances in priority order
      this.rules = [];

      // Fallback default rule instance
      this.defaultRule = new DefaultPickupRule();

      // Register default set of US regional rules
      this._registerDefaultRules();

    }

    /**
     * Registers the standard suite of pickup rules in priority order.
     *
     * @private
     * @method _registerDefaultRules
     * @returns {void}
     */
    _registerDefaultRules() {

      // Register Cake 6"/8" rule strategy
      this.registerRule(new Cake6To8Rule());

      // Register Cake 10"/Quarter sheet rule strategy
      this.registerRule(new Cake10QuarterRule());

      // Register Cake 1/2 and Full sheet rule strategy
      this.registerRule(new CakeHalfFullRule());

      // Register Bakery rule strategy
      this.registerRule(new BakeryRule());

      // Register Drinks rule strategy
      this.registerRule(new DrinkRule());

    }

    /**
     * Registers a rule strategy instance with the factory.
     *
     * @method registerRule
     * @param {Object} ruleInstance - An instance of AbstractPickupRule.
     * @param {boolean} [prepend=false] - Whether to prepend with higher priority.
     * @returns {void}
     */
    registerRule(ruleInstance, prepend = false) {

      // Validate rule instance exists and implements isApplicable
      if (!ruleInstance || typeof ruleInstance.isApplicable !== 'function') {

        // Return early on invalid instance
        return;

      }

      // Check if prepend priority is requested
      if (prepend) {

        // Add to front of array
        this.rules.unshift(ruleInstance);

      // Standard append priority
      } else {

        // Add to end of array
        this.rules.push(ruleInstance);

      }

    }

    /**
     * Inspects the context and returns the first applicable PickupRule strategy.
     *
     * @method getRule
     * @param {Object} context - The current domain PickupContext.
     * @returns {Object} The matching AbstractPickupRule instance.
     */
    getRule(context) {

      // Validate context parameter
      if (!context) {

        // Return default fallback rule
        return this.defaultRule;

      }

      // Iterate through registered rules in order of precedence
      for (const rule of this.rules) {

        // Check if rule matches context conditions
        if (rule.isApplicable(context)) {

          // Return matching rule strategy
          return rule;

        }

      }

      // Return default fallback rule when no specific rule matches
      return this.defaultRule;

    }

    /**
     * Creates an evaluated resolver object adhering to the contract expected
     * by components and cart validation.
     *
     * @method createResolver
     * @param {Object} context - The current PickupContext.
     * @param {Date} [now=new Date()] - Reference time.
     * @returns {Object} Resolver object.
     */
    createResolver(context, now = new Date()) {

      // Select active rule strategy from context
      const rule = this.getRule(context);

      // Determine current reference date
      const referenceNow = now || (context && typeof context.getNow === 'function' ? context.getNow() : new Date());

      // Compute minimum selectable date
      const minDate = rule ? rule.getMinDate(context, referenceNow) : referenceNow;

      // Return unified resolver facade
      return {

        // Expose product family
        family: context ? context.productFamily : null,

        // Expose variant rule key
        variantRule: context ? context.variantRule : null,

        // Expose active rule name identifier
        activeRuleName: rule ? rule.name : 'Unknown',

        // Expose calculated minimum date
        minDate: minDate,

        // Bound helper to compute windows for any date
        windowsFor: (date, currentNow = referenceNow) => {

          // Delegate to rule strategy windowsFor method
          return rule ? rule.windowsFor(date, context, currentNow) : [];

        },

        // Bound helper to determine if date is allowed
        isAllowed: (date) => {

          // Delegate to rule strategy isAllowed method
          return rule ? rule.isAllowed(date, context, referenceNow) : true;

        },

        // Bound helper to find next earliest valid date and slots
        autoAdvance: (date) => {

          // Delegate to rule strategy autoAdvance method
          return rule ? rule.autoAdvance(date, context, referenceNow) : { date, slots: [] };

        },

        // Why a chosen Pickup day is rejected
        rejectionReason: (date) => {

          // Delegate to the rule
          return rule ? rule.rejectionReason(date, context, referenceNow) : null;

        }

      };

    }

  }

  // Create default singleton factory instance
  const defaultFactory = new PickupRuleFactory();

  /* ==========================================================================
     SECTION 5: PICKUP REGIONS REGISTRY
     ========================================================================== */

  /**
   * Registry of regional pickup resolvers.
   *
   * @constant
   * @type {Object.<string, { resolve: Function }>}
   */
  const PICKUP_REGIONS = {

    // United States region configuration
    us: {

      /**
       * Resolves pickup rules, minimum selectable date, and time slot generators for US region.
       *
       * @method resolve
       * @param {Object|PickupContext} [ctx={}] - Domain context or raw context object.
       * @param {Date} [now=new Date()] - Reference date representing current time.
       * @param {string|null} [tz=null] - Optional timezone string.
       * @returns {Object} Evaluated resolver containing minDate, windowsFor, isAllowed, and autoAdvance.
       */
      resolve(ctx = {}, now = new Date(), tz = null) {

        // Determine if incoming context is already a PickupContext instance
        const isContextInstance = ctx instanceof PickupContext;

        // Wrap in PickupContext if raw object was passed
        const context = isContextInstance
          ? ctx
          : new PickupContext({

              // Pass product ID
              productId: ctx.productId,

              // Pass product type
              productType: ctx.productType,

              // Pass tags array
              tags: ctx.tags,

              // Pass options list
              options: ctx.options,

              // Pass variants list
              variants: ctx.variants,

              // Pass selected variant ID
              selectedVariantId: ctx.selectedVariantId || ctx.variantId,

              // Pass variant title
              selectedVariantTitle: ctx.selectedVariantTitle || ctx.variantTitle,

              // Pass region identifier
              region: 'us',

              // Pass timezone
              timezone: tz || ctx.timezone || null

            });

        // Override variantRule if explicitly provided in legacy context payload
        if (ctx.variantRule && !context.variantRule) {

          // Assign explicit variant rule
          context.variantRule = ctx.variantRule;

        }

        // Delegate resolution to the OOP rule factory
        return defaultFactory.createResolver(context, now);

      }

    }

  };

  /* ==========================================================================
     SECTION 6: WEB COMPONENTS, SUB-MANAGERS & GUARDS
     ========================================================================== */

  /**
   * Helper class managing all DOM manipulation, error display, and dropdown rendering.
   *
   * @class PickupUIManager
   */
  class PickupUIManager {

    /**
     * Initializes a new instance of PickupUIManager.
     *
     * @constructor
     * @param {HTMLElement} hostElement - The parent custom element container.
     */
    constructor(hostElement) {

      // Store reference to parent custom element host
      this.host = hostElement;

    }

    /**
     * Retrieves the native date input element.
     *
     * @member {HTMLInputElement|null} dateInput
     */
    get dateInput() {

      // Query date input inside host element
      return this.host.querySelector('input[type="date"]');

    }

    /**
     * Retrieves the native time select element.
     *
     * @member {HTMLSelectElement|null} timeSelect
     */
    get timeSelect() {

      // Query select element inside host element
      return this.host.querySelector('select');

    }

    /**
     * Retrieves the error message container element.
     *
     * @member {HTMLElement|null} errorEl
     */
    get errorEl() {

      // Query error container element
      return this.host.querySelector('.cart-pickup__error');

    }

    /**
     * Retrieves the error text target element.
     *
     * @member {HTMLElement|null} errorTextEl
     */
    get errorTextEl() {

      // Query error text child element
      return this.host.querySelector('.cart-pickup__error-text');

    }

    /**
     * Retrieves the collection summary banner element.
     *
     * @member {HTMLElement|null} collectingEl
     */
    get collectingEl() {

      // Query collecting summary container element
      return this.host.querySelector('.cart-pickup__collecting');

    }

    /**
     * Retrieves localized text strings from host dataset attributes.
     *
     * @member {Object} text
     */
    get text() {

      // Return localized strings with sensible fallbacks
      return {

        // Placeholder when selecting time
        windowPlaceholder: this.host.dataset.textWindowPlaceholder || 'Select a time window',

        // Message when no date is picked
        noDate: this.host.dataset.textNoDate || 'Select a day first',

        // Message when day has no available slots
        unavailable: this.host.dataset.textUnavailable || "We're not open for pickup on [day]. Pick another day.",

        // Same-day cutoff or a finished today
        tooSoon: this.host.dataset.textTooSoon || 'Please choose [date] or later.',

        // Fixed notice of 2 or 4 calendar days
        leadTime: this.host.dataset.textLeadTime || 'This item needs [days] days notice. Please choose [date] or later.',

        // Required field validation error
        required: this.host.dataset.textRequired || 'Please choose a pickup day and collection window.',

        // Collecting status prefix
        collecting: this.host.dataset.textCollecting || 'Collecting'

      };

    }

    /**
     * Sets the minimum allowed date on the date input.
     *
     * @method setMinDate
     * @param {string} isoDate - Date formatted as YYYY-MM-DD.
     * @returns {void}
     */
    setMinDate(isoDate) {

      // Check if date input exists
      if (this.dateInput) {

        // Assign min attribute
        this.dateInput.min = isoDate;

        // Ensure date input is enabled
        this.dateInput.disabled = false;

      }

    }

    /**
     * Assigns the value of the date input.
     *
     * @method setDateValue
     * @param {string} isoDate - Date formatted as YYYY-MM-DD.
     * @returns {void}
     */
    setDateValue(isoDate) {

      // Check if date input exists
      if (this.dateInput) {

        // Assign value
        this.dateInput.value = isoDate || '';

      }

    }

    /**
     * Resets the time dropdown to a disabled single-placeholder option state.
     *
     * @method resetTimeDropdown
     * @param {string} placeholderText - Text to display in the placeholder option.
     * @returns {void}
     */
    resetTimeDropdown(placeholderText) {

      // Check if time select element exists
      if (!this.timeSelect) return;

      // Populate dropdown with single disabled placeholder option
      this.timeSelect.innerHTML = `<option value="" disabled selected>${this.escapeHtml(placeholderText)}</option>`;

      // Disable select dropdown
      this.timeSelect.disabled = true;

    }

    /**
     * Populates the time dropdown with slot options and selects an optional preselected slot.
     *
     * @method populateTimeDropdown
     * @param {string[]} slots - Array of slot strings.
     * @param {string} [selectedSlot=''] - The slot to select.
     * @returns {void}
     */
    populateTimeDropdown(slots, selectedSlot = '') {

      // Check if time select element exists
      if (!this.timeSelect) return;

      // Check if slots array is empty
      if (!slots || slots.length === 0) {

        // Keep the select a time control. The error sits under the fields.
        this.resetTimeDropdown(this.text.windowPlaceholder);

        // Return early
        return;

      }

      // Initialize options HTML string with default prompt option
      let html = `<option value="" disabled ${!selectedSlot ? 'selected' : ''}>${this.escapeHtml(this.text.windowPlaceholder)}</option>`;

      // Iterate through each available slot
      slots.forEach((slot) => {

        // Determine if slot matches preselected value
        const isSelected = selectedSlot && slot === selectedSlot;

        // Format slot display text with en-dash separator
        const display = slot.replace(/\s+-\s+/, ' \u2013 ');

        // Append option element
        html += `<option value="${this.escapeHtml(slot)}" ${isSelected ? 'selected' : ''}>${this.escapeHtml(display)}</option>`;

      });

      // Assign options HTML to dropdown
      this.timeSelect.innerHTML = html;

      // Enable dropdown for user selection
      this.timeSelect.disabled = false;

    }

    /**
     * Displays a validation error message in the UI.
     *
     * @method showError
     * @param {string} message - The error message string.
     * @returns {void}
     */
    showError(message) {

      // Check if error container exists
      if (!this.errorEl) return;

      // Assign message text to error text element
      if (this.errorTextEl) {

        // Update text content
        this.errorTextEl.textContent = message;

      }

      // Unhide error container
      this.errorEl.hidden = false;

      // Mark date input as invalid for accessibility
      if (this.dateInput) {

        // Set aria-invalid attribute
        this.dateInput.setAttribute('aria-invalid', 'true');

      }

    }

    /**
     * Clears error message and hides the error container.
     *
     * @method clearError
     * @returns {void}
     */
    clearError() {

      // Check if error container exists
      if (!this.errorEl) return;

      // Clear text inside error text element
      if (this.errorTextEl) {

        // Empty text content
        this.errorTextEl.textContent = '';

      }

      // Hide error container
      this.errorEl.hidden = true;

      // Remove aria-invalid attribute
      if (this.dateInput) {

        // Remove aria-invalid
        this.dateInput.removeAttribute('aria-invalid');

      }

    }

    /**
     * Renders or hides the collection status summary banner.
     *
     * @method renderCollecting
     * @param {string} date - Selected date string (YYYY-MM-DD).
     * @param {string} time - Selected time slot window string.
     * @returns {void}
     */
    renderCollecting(date, time) {

      // Check if collecting element exists
      if (!this.collectingEl) return;

      // If either date or time is missing, hide banner
      if (!date || !time) {

        // Hide collecting element
        this.collectingEl.hidden = true;

        // Clear slot text
        const slotEl = this.collectingEl.querySelector('.cart-pickup__collecting-slot');

        // If slot element found, clear it
        if (slotEl) slotEl.textContent = '';

        // Return early
        return;

      }

      // Parse date components for formatted display
      const parts = String(date).split('-').map(Number);

      // Construct local Date instance
      const parsedDate = parts.length === 3 ? new Date(parts[0], parts[1] - 1, parts[2]) : null;

      // Format date into human-readable weekday and month string
      const dayText = parsedDate
        ? parsedDate.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })
        : date;

      // Locate label element
      const labelEl = this.collectingEl.querySelector('.cart-pickup__collecting-label');

      // Populate label text
      if (labelEl) labelEl.textContent = this.text.collecting;

      // Locate slot text element
      const slotEl = this.collectingEl.querySelector('.cart-pickup__collecting-slot');

      // Format time separator with en-dash
      const formattedTime = String(time).replace(/\s+-\s+/, ' \u2013 ');

      // Populate slot text
      if (slotEl) slotEl.textContent = `${dayText}, ${formattedTime}`;

      // Unhide collecting summary banner
      this.collectingEl.hidden = false;

    }

    /**
     * Escapes unsafe characters for HTML insertion.
     *
     * @method escapeHtml
     * @param {string} str - Raw string.
     * @returns {string} Sanitized string.
     */
    escapeHtml(str) {

      // Create dummy element
      const div = document.createElement('div');

      // Set text content
      div.textContent = str;

      // Return sanitized HTML
      return div.innerHTML;

    }

  }

  /**
   * Helper class managing draft persistence in sessionStorage and auto-move logic.
   *
   * @class PickupDraftManager
   */
  class PickupDraftManager {

    /**
     * Prefix for sessionStorage keys.
     * @constant {string}
     */
    static STORAGE_PREFIX = 'product-pickup:';

    /**
     * Builds storage key for product and variant.
     *
     * @method getStorageKey
     * @param {string} productId - Product ID.
     * @param {string} variantId - Variant ID.
     * @returns {string} Storage key string.
     */
    static getStorageKey(productId, variantId) {

      // Return composed storage key
      return `${PickupDraftManager.STORAGE_PREFIX}${productId}:${variantId}`;

    }

    /**
     * Saves date and time selection to sessionStorage.
     *
     * @method saveDraft
     * @param {string} productId - Product ID.
     * @param {string} variantId - Variant ID.
     * @param {string} date - Selected date.
     * @param {string} time - Selected time.
     * @returns {void}
     */
    static saveDraft(productId, variantId, date, time) {

      // Check if sessionStorage is accessible
      try {

        // Build storage key
        const key = PickupDraftManager.getStorageKey(productId, variantId);

        // If both fields populated, store value
        if (date && time) {

          // Store delimited value
          sessionStorage.setItem(key, `${date}|${time}`);

        // Otherwise clear draft
        } else {

          // Remove item
          sessionStorage.removeItem(key);

        }

      // Suppress storage errors
      } catch (e) { }

    }

    /**
     * Reads saved draft from sessionStorage.
     *
     * @method readDraft
     * @param {string} productId - Product ID.
     * @param {string} variantId - Variant ID.
     * @returns {string} Delimited draft string or empty.
     */
    static readDraft(productId, variantId) {

      // Access sessionStorage safely
      try {

        // Build storage key
        const key = PickupDraftManager.getStorageKey(productId, variantId);

        // Return stored item or empty string
        return sessionStorage.getItem(key) || '';

      // Suppress storage errors
      } catch (e) {

        // Return empty string fallback
        return '';

      }

    }

    /**
     * Clears draft from sessionStorage.
     *
     * @method clearDraft
     * @param {string} productId - Product ID.
     * @param {string} variantId - Variant ID.
     * @returns {void}
     */
    static clearDraft(productId, variantId) {

      // Access sessionStorage safely
      try {

        // Build storage key
        const key = PickupDraftManager.getStorageKey(productId, variantId);

        // Remove item
        sessionStorage.removeItem(key);

      // Suppress storage errors
      } catch (e) { }

    }

  }

  /**
   * Helper class managing synchronization of hidden cart form inputs.
   *
   * @class PickupFormSync
   */
  class PickupFormSync {

    /**
     * Initializes a new instance of PickupFormSync.
     *
     * @constructor
     * @param {HTMLElement} hostElement - The parent custom element.
     */
    constructor(hostElement) {

      // Store reference to parent custom element
      this.host = hostElement;

    }

    /**
     * Retrieves hidden input element for Pickup Date.
     *
     * @member {HTMLInputElement|null} hiddenDateEl
     */
    get hiddenDateEl() {

      // Query hidden input for Pickup Date
      return this.host.querySelector('input[type="hidden"][name="properties[Pickup Date]"]');

    }

    /**
     * Retrieves hidden input element for Pickup Time.
     *
     * @member {HTMLInputElement|null} hiddenTimeEl
     */
    get hiddenTimeEl() {

      // Query hidden input for Pickup Time
      return this.host.querySelector('input[type="hidden"][name="properties[Pickup Time]"]');

    }

    /**
     * Synchronizes current date and time selections into hidden inputs.
     *
     * @method sync
     * @param {string} date - Selected date string.
     * @param {string} time - Selected time string.
     * @returns {void}
     */
    sync(date, time) {

      // Check if hidden date input exists
      if (this.hiddenDateEl) {

        // Assign value
        this.hiddenDateEl.value = date || '';

      }

      // Check if hidden time input exists
      if (this.hiddenTimeEl) {

        // Assign value
        this.hiddenTimeEl.value = time || '';

      }

    }

  }

  /**
   * Helper class intercepting Add to Cart button clicks if pickup options are missing.
   *
   * @class PickupAddToCartGuard
   */
  class PickupAddToCartGuard {

    /**
     * Initializes a new instance of PickupAddToCartGuard.
     *
     * @constructor
     * @param {ProductPickup} component - Reference to custom element.
     */
    constructor(component) {

      // Store component reference
      this.component = component;

      // Bind handler to maintain scope
      this.handleClick = this.handleClick.bind(this);

    }

    /**
     * Attaches click capture listener.
     *
     * @method mount
     * @returns {void}
     */
    mount() {

      // Add capture phase click listener to document
      document.addEventListener('click', this.handleClick, true);

    }

    /**
     * Detaches click capture listener.
     *
     * @method unmount
     * @returns {void}
     */
    unmount() {

      // Remove capture phase click listener from document
      document.removeEventListener('click', this.handleClick, true);

    }

    /**
     * Intercepts clicks on xo-cart-add elements.
     *
     * @method handleClick
     * @param {MouseEvent} event - Click event.
     * @returns {void}
     */
    handleClick(event) {

      // Find closest add-to-cart button
      const addEl = event.target?.closest?.('xo-cart-add');

      // If not an add-to-cart button, exit
      if (!addEl) return;

      // Find enclosing product component
      const productEl = addEl.closest('xo-product');

      // Verify button belongs to the same product as this pickup component
      if (!productEl || productEl !== this.component.closest('xo-product')) return;

      // If both pickup date and time are selected, allow submission only when the pair is bookable
      if (this.component.context.selectedDate && this.component.context.selectedTime) {

        // Active rule
        const resolver = this.component.resolve();

        // Chosen Pickup day
        const chosen = this.component.parseISODate(this.component.context.selectedDate);

        // Rejection, if the day is too soon or closed
        const reason = resolver && chosen ? resolver.rejectionReason(chosen) : { code: 'closed', earliest: new Date() };

        // Windows on that day
        const slots = resolver && chosen ? resolver.windowsFor(chosen) : [];

        // Valid pair may be added
        if (!reason && slots.includes(this.component.context.selectedTime)) {

          // Allow add-to-cart operation
          return;

        }

        // Prevent add-to-cart action
        event.preventDefault();

        // Stop event propagation
        event.stopPropagation();

        // Explain the rejection, including a window that is no longer bookable
        if (chosen) {

          // Matching sentence
          this.component.showRejection(chosen, reason);

        }

        // Exit
        return;

      }

      // Prevent add-to-cart action
      event.preventDefault();

      // Stop event propagation
      event.stopPropagation();

      // Display required field error message
      this.component.ui.showError(this.component.ui.text.required);

      // Focus the date input field
      this.component.ui.dateInput?.focus();

    }

  }

  /**
   * Primary ProductPickup Custom Web Component.
   * Controller connecting PickupContext, PickupRuleFactory, and Sub-Managers.
   *
   * @class ProductPickup
   * @extends HTMLElement
   */
  class ProductPickup extends HTMLElement {

    /**
     * Initializes custom element lifecycle.
     *
     * @constructor
     */
    constructor() {

      // Invoke super constructor
      super();

      // Initialize UI manager sub-controller
      this.ui = new PickupUIManager(this);

      // Initialize form sync sub-controller
      this.formSync = new PickupFormSync(this);

      // Initialize add-to-cart guard sub-controller
      this.guard = new PickupAddToCartGuard(this);

      // Storage for rule factory instance
      this.factory = null;

      // Storage for domain context instance
      this.context = null;

      // Storage for mediator instance
      this.mediator = null;

      // Storage for context unsubscription teardown
      this._unsubContext = null;

    }

    /**
     * Resolves the rule factory instance.
     *
     * @method getFactory
     * @returns {Object} PickupRuleFactory instance.
     */
    getFactory() {

      // Check if factory is already instantiated
      if (!this.factory) {

        // Instantiate rule factory
        this.factory = new PickupRuleFactory();

      }

      // Return factory instance
      return this.factory;

    }

    /**
     * Evaluates current active rule and returns evaluated resolver facade.
     *
     * @method resolve
     * @returns {Object|null} Evaluated resolver.
     */
    resolve() {

      // Retrieve factory instance
      const factory = this.getFactory();

      // If factory or context is missing, return null
      if (!factory || !this.context) return null;

      // Create evaluated resolver
      return factory.createResolver(this.context, this.context.getNow());

    }

    /**
     * Extracts initial product context from embedded JSON or dataset attributes.
     *
     * @method parseInitialContext
     * @returns {Object} Context payload.
     */
    parseInitialContext() {

      // Try reading embedded JSON script tag
      try {

        // Query embedded pickup context script tag
        const ctxEl = this.querySelector('script[data-pickup-context]');

        // If script element exists
        if (ctxEl) {

          // Parse JSON payload
          const parsed = JSON.parse(ctxEl.textContent || '{}');

          // Return parsed context
          return {

            // Product ID
            productId: parsed.productId || this.dataset.productId || '',

            // Product type
            productType: parsed.productType || this.dataset.productType || '',

            // Tags
            tags: parsed.tags || (this.dataset.tags || '').split(',').filter(Boolean),

            // Options
            options: parsed.options || [],

            // Variants
            variants: parsed.variants || [],

            // Selected variant ID
            selectedVariantId: parsed.selectedVariantId || '',

            // Region
            region: this.dataset.region || 'us',

            // Timezone
            timezone: this.dataset.timezone || null

          };

        }

      // Catch JSON parse errors
      } catch (e) { }

      // Fallback: construct payload from dataset attributes
      return {

        // Product ID from dataset or closest xo-product
        productId: this.dataset.productId || this.closest('xo-product')?.getAttribute('xo-product-id') || '',

        // Product type from dataset
        productType: this.dataset.productType || '',

        // Tags from dataset
        tags: (this.dataset.tags || '').split(',').filter(Boolean),

        // Empty options array fallback
        options: [],

        // Empty variants array fallback
        variants: [],

        // Variant ID fallback
        selectedVariantId: '',

        // Region from dataset
        region: this.dataset.region || 'us',

        // Timezone from dataset
        timezone: this.dataset.timezone || null

      };

    }

    /**
     * Parses an ISO date into a local Date.
     *
     * @method parseISODate
     * @param {string} value - YYYY-MM-DD.
     * @returns {Date|null} Local date, or null.
     */
    parseISODate(value) {

      // Split the calendar parts
      const parts = String(value || '').split('-').map(Number);

      // Require year, month, and day
      if (parts.length !== 3 || parts.some((part) => !part)) return null;

      // Build a local midnight date
      return new Date(parts[0], parts[1] - 1, parts[2]);

    }

    /**
     * Formats a date for an error sentence.
     *
     * @method formatLongDate
     * @param {Date} date - Date to format.
     * @returns {string} Weekday, month, and day.
     */
    formatLongDate(date) {

      // Readable store-facing date
      return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

    }

    /**
     * Builds the error sentence for a rejected Pickup day.
     *
     * @method formatRejection
     * @param {{ code: string, earliest: Date, days?: number }} reason - Rejection.
     * @param {Date} chosenDate - The day the customer picked.
     * @returns {string} Error sentence.
     */
    formatRejection(reason, chosenDate) {

      // Earliest Pickup day label
      const earliestLabel = this.formatLongDate(reason.earliest);

      // Fixed 2-day or 4-day notice
      if (reason.code === 'notice') {

        // Notice sentence
        return this.ui.text.leadTime.replace('[days]', String(reason.days)).replace('[date]', earliestLabel);

      }

      // A day with no Collection windows
      if (reason.code === 'closed') {

        // Weekday name of the chosen day
        const dayName = chosenDate.toLocaleDateString('en-US', { weekday: 'long' });

        // Closed-day sentence
        return this.ui.text.unavailable.replace('[day]', dayName).replace('{{ day }}', dayName);

      }

      // Same-day cutoff or a finished today
      return this.ui.text.tooSoon.replace('[date]', earliestLabel);

    }

    /**
     * Leaves a rejected choice in place and blocks the cart properties.
     *
     * @method showRejection
     * @param {Date} chosenDate - Chosen Pickup day.
     * @param {{ code: string, earliest: Date, days?: number }} reason - Rejection.
     * @returns {void}
     */
    showRejection(chosenDate, reason) {

      // A stale window on an otherwise open day still needs a sentence
      const safeReason = reason || { code: 'too_soon', earliest: chosenDate };

      // Show the matching sentence
      this.ui.showError(this.formatRejection(safeReason, chosenDate));

      // Hide the collecting banner
      this.ui.renderCollecting('', '');

      // Do not submit the rejected pair
      this.formSync.sync('', '');

    }

    /**
     * Shows the Earliest Pickup day with no Collection window selected.
     *
     * @method prefillEarliest
     * @param {Object} resolver - Active rule resolver.
     * @returns {void}
     */
    prefillEarliest(resolver) {

      // Earliest Pickup day
      const iso = this.toISODate(resolver.minDate);

      // Bookable windows on that day
      const slots = resolver.windowsFor(resolver.minDate);

      // No error on a first visit
      this.ui.clearError();

      // Show the earliest day
      this.ui.setDateValue(iso);

      // Time stays empty
      this.ui.populateTimeDropdown(slots);

      // Remember the prefilled day only
      this.context.selectedDate = iso;

      // No window chosen
      this.context.selectedTime = '';

      // Banner waits for a window
      this.ui.renderCollecting(iso, '');

      // Date alone cannot be added to cart
      this.formSync.sync(iso, '');

    }

    /**
     * Converts a Date to ISO format YYYY-MM-DD.
     *
     * @method toISODate
     * @param {Date} d - Date instance.
     * @returns {string} Formatted date.
     */
    toISODate(d) {

      // Extract month with leading zero
      const m = String(d.getMonth() + 1).padStart(2, '0');

      // Extract day with leading zero
      const day = String(d.getDate()).padStart(2, '0');

      // Return ISO date string
      return `${d.getFullYear()}-${m}-${day}`;

    }

    /**
     * Custom element lifecycle callback when attached to the DOM.
     *
     * @override
     * @method connectedCallback
     * @returns {void}
     */
    connectedCallback() {

      // Parse initial context data
      const initialData = this.parseInitialContext();

      // Instantiate PickupContext
      this.context = new PickupContext(initialData);

      // Instantiate VariantChangeMediator
      this.mediator = new VariantChangeMediator(this.context, this);

      // Mount mediator listeners
      this.mediator.mount();

      // Subscribe to context state changes
      this._unsubContext = this.context.subscribe((event) => {

        // Handle state update based on event type
        this.onContextUpdate(event);

      });

      // Set initial minimum date on date input
      this.applyMinDate();

      // Attach change listener to date input
      this.ui.dateInput?.addEventListener('change', () => {

        // Read value from input
        const value = this.ui.dateInput ? this.ui.dateInput.value : '';

        // Update context date
        this.context.updateDate(value);

      });

      // Attach change listener to time select
      this.ui.timeSelect?.addEventListener('change', () => {

        // Read value from select
        const value = this.ui.timeSelect ? this.ui.timeSelect.value : '';

        // Update context time
        this.context.updateTime(value);

      });

      // Mount Add-to-Cart guard click listener
      this.guard.mount();

      // Attempt to restore saved draft or auto-advance on next tick
      setTimeout(() => this.tryRestoreOrAutoMove(), 0);

    }

    /**
     * Custom element lifecycle callback when removed from the DOM.
     *
     * @override
     * @method disconnectedCallback
     * @returns {void}
     */
    disconnectedCallback() {

      // Save draft before element destruction
      if (this.context) {

        // Persist draft
        PickupDraftManager.saveDraft(
          this.context.productId,
          this.context.selectedVariantId,
          this.context.selectedDate,
          this.context.selectedTime
        );

      }

      // Unmount mediator listeners
      if (this.mediator) {

        // Teardown mediator
        this.mediator.unmount();

      }

      // Unmount add-to-cart guard listener
      this.guard.unmount();

      // Unsubscribe from context changes
      if (this._unsubContext) {

        // Invoke teardown closure
        this._unsubContext();

      }

    }

    /**
     * Updates the min attribute on the date input based on the active rule.
     *
     * @method applyMinDate
     * @returns {void}
     */
    applyMinDate() {

      // If date input is missing, exit
      if (!this.ui.dateInput) return;

      // Evaluate active rule resolver
      const resolver = this.resolve();

      // If resolver is missing, exit
      if (!resolver) return;

      // Set min date attribute on input
      this.ui.setMinDate(this.toISODate(resolver.minDate));

    }

    /**
     * Reacts to events emitted by the PickupContext.
     *
     * @method onContextUpdate
     * @param {Object} event - State change event.
     * @returns {void}
     */
    onContextUpdate(event) {

      // Handle variant change event
      if (event.type === 'variant_change') {

        // Re-evaluate min date for new variant
        this.applyMinDate();

        // Attempt to restore draft for this variant or auto-advance
        this.tryRestoreOrAutoMove();

      // Handle date change event
      } else if (event.type === 'date_change') {

        // Handle date selection update
        this.handleDateChange();

      // Handle time change event
      } else if (event.type === 'time_change') {

        // Render collection summary banner
        this.ui.renderCollecting(this.context.selectedDate, this.context.selectedTime);

        // Sync hidden cart inputs
        this.formSync.sync(this.context.selectedDate, this.context.selectedTime);

        // Persist draft
        PickupDraftManager.saveDraft(
          this.context.productId,
          this.context.selectedVariantId,
          this.context.selectedDate,
          this.context.selectedTime
        );

      // Handle selection reset
      } else if (event.type === 'selection_reset') {

        // Clear date input
        this.ui.setDateValue('');

        // Reset time dropdown
        this.ui.resetTimeDropdown(this.ui.text.noDate);

        // Hide collecting banner
        this.ui.renderCollecting('', '');

        // Sync empty hidden inputs
        this.formSync.sync('', '');

      }

    }

    /**
     * Handles date change re-evaluations.
     *
     * @method handleDateChange
     * @returns {void}
     */
    handleDateChange() {

      // Resolve evaluated rule
      const resolver = this.resolve();

      // If date is empty
      if (!this.context.selectedDate) {

        // Reset dropdown to "Select day first"
        this.ui.resetTimeDropdown(this.ui.text.noDate);

        // Update collecting banner
        this.ui.renderCollecting('', '');

        // Sync hidden inputs
        this.formSync.sync('', '');

        // Exit
        return;

      }

      // Parse date components
      const parts = this.context.selectedDate.split('-').map(Number);

      // If invalid date format, exit
      if (parts.length !== 3) return;

      // Create local Date instance
      const parsedDate = new Date(parts[0], parts[1] - 1, parts[2]);

      // A new day has no Collection window yet
      this.context.selectedTime = '';

      // If resolver is active
      if (resolver) {

        // Why this day cannot be collected
        const reason = resolver.rejectionReason(parsedDate);

        // Leave the chosen day in place
        if (reason) {

          // Reset the time dropdown
          this.ui.resetTimeDropdown(this.ui.text.windowPlaceholder);

          // Show the matching error
          this.showRejection(parsedDate, reason);

          // Exit without rewriting the date
          return;

        }

        // Bookable windows for the chosen day
        const slots = resolver.windowsFor(parsedDate);

        // Date is valid and has open slots; clear errors
        this.ui.clearError();

        // Populate dropdown without preselecting a window
        this.ui.populateTimeDropdown(slots);

        // Render collecting banner
        this.ui.renderCollecting(this.context.selectedDate, '');

        // Sync hidden inputs
        this.formSync.sync(this.context.selectedDate, '');

        // Exit
        return;

      }

      // Default fallback
      this.ui.clearError();

      // Reset dropdown
      this.ui.resetTimeDropdown(this.ui.text.windowPlaceholder);

    }

    /**
     * Restores saved draft or auto-advances to the nearest valid slot.
     *
     * @method tryRestoreOrAutoMove
     * @returns {void}
     */
    tryRestoreOrAutoMove() {

      // Read saved draft for this variant
      const draft = PickupDraftManager.readDraft(this.context.productId, this.context.selectedVariantId);

      // Evaluate active resolver
      const resolver = this.resolve();

      // If resolver is not active, exit
      if (!resolver) return;

      // A chosen Collection window is never rewritten on variant change
      if (this.context.selectedTime && this.context.selectedDate) {

        // Parse the chosen day
        const chosen = this.parseISODate(this.context.selectedDate);

        // Rejection for the new variant, if any
        const reason = chosen ? resolver.rejectionReason(chosen) : { code: 'closed', earliest: resolver.minDate };

        // Windows still offered on that day
        const slots = chosen ? resolver.windowsFor(chosen) : [];

        // The pair is still bookable
        if (chosen && !reason && slots.includes(this.context.selectedTime)) {

          // Keep the date
          this.ui.setDateValue(this.context.selectedDate);

          // Keep the window
          this.ui.populateTimeDropdown(slots, this.context.selectedTime);

          // Clear any previous error
          this.ui.clearError();

          // Show the collecting banner
          this.ui.renderCollecting(this.context.selectedDate, this.context.selectedTime);

          // Sync the valid pair
          this.formSync.sync(this.context.selectedDate, this.context.selectedTime);

          // Exit
          return;

        }

        // Leave the customer's choice and explain why it no longer works
        this.ui.setDateValue(this.context.selectedDate);

        // Show the matching error
        this.showRejection(chosen || resolver.minDate, reason);

        // Exit
        return;

      }

      // If a draft exists
      if (draft) {

        // Split date and time tokens
        const [dateVal, timeVal] = draft.split('|');

        // Parse date components
        const parts = String(dateVal).split('-').map(Number);

        // Create Date instance
        const parsed = parts.length === 3 ? new Date(parts[0], parts[1] - 1, parts[2]) : null;

        // If date is valid and permitted
        if (parsed && resolver.isAllowed(parsed)) {

          // Retrieve open slots
          const slots = resolver.windowsFor(parsed);

          // If draft slot is still open
          if (slots.includes(timeVal)) {

            // Restore date on context
            this.context.selectedDate = dateVal;

            // Restore date input
            this.ui.setDateValue(dateVal);

            // Populate dropdown with chosen slot
            this.ui.populateTimeDropdown(slots, timeVal);

            // Restore time on context
            this.context.selectedTime = timeVal;

            // Render collecting banner
            this.ui.renderCollecting(dateVal, timeVal);

            // Sync hidden inputs
            this.formSync.sync(dateVal, timeVal);

            // Exit
            return;

          }

        }

      }

      // No bookable saved pair: load like a first visit
      this.prefillEarliest(resolver);

    }

  }

  /* ==========================================================================
     SECTION 7: REGISTRATION & EXPOSURE
     ========================================================================== */

  // Define custom element if not already registered
  if (!customElements.get('product-pickup')) {

    // Register product-pickup custom element
    customElements.define('product-pickup', ProductPickup);

  }

  // Expose onto window for browser runtime
  if (typeof window !== 'undefined') {

    // Attach PickupConfig
    window.PickupConfig = PickupConfig;

    // Attach PickupContext
    window.PickupContext = PickupContext;

    // Attach VariantChangeMediator
    window.VariantChangeMediator = VariantChangeMediator;

    // Attach PickupRules
    window.PickupRules = PickupRules;

    // Attach PickupRuleFactory
    window.PickupRuleFactory = PickupRuleFactory;

    // Attach defaultFactory singleton
    window.PickupRuleFactory.defaultFactory = defaultFactory;

    // Attach PICKUP_REGIONS
    window.PICKUP_REGIONS = PICKUP_REGIONS;

  }

  // Support CommonJS export in test/node environments
  if (typeof module !== 'undefined' && module.exports) {

    // Export module elements
    module.exports = {

      // Export PickupConfig
      PickupConfig,

      // Export PickupContext
      PickupContext,

      // Export VariantChangeMediator
      VariantChangeMediator,

      // Export PickupRules
      PickupRules,

      // Export PickupRuleFactory
      PickupRuleFactory,

      // Export defaultFactory
      defaultFactory,

      // Export PICKUP_REGIONS
      PICKUP_REGIONS,

      // Export ProductPickup
      ProductPickup

    };

  }

// Self execute IIFE
})();
